import { useEffect, useState } from 'react'

import { requestApi } from './catalog'
import { readWebSession } from './web-session'

import './commerce-shop.css'

interface ProductSummary {
  id: string
  name: string
  subtitle: string
  packageKind: 'single' | 'multi' | 'combo'
  coverUrl: string | null
  minPriceCents: number
  skuCount: number
  soldCount: number
}

interface ProductDetail {
  id: string
  name: string
  subtitle: string
  description: string
  coverUrl: string | null
  skus: Array<{ id: string; name: string; priceCents: number; validDays: number | null }>
  components: Array<{ serviceId: string; serviceName: string; quantity: number; sameTimeGroup: string | null }>
  storeScopes: Array<{ storeId: string; name: string }>
}

interface CartItem {
  id: string
  skuId: string
  productName: string
  skuName: string
  quantity: number
  priceCents: number
  salesStoreId: string
  salesStoreName: string
  purchaseable: boolean
}

interface Quote {
  quoteId: string
  cartVersion: string
  cashTotalCents: number
  couponTotalCents: number
  pointsUsed: number
  pointsValueCents: number
  subOrders: Array<{
    salesStoreId: string
    salesStoreName: string
    couponCents: number
    cashCents: number
    lines: Array<{ lineKey: string; productName: string; quantity: number; cashCents: number }>
  }>
}

interface CartVoucher {
  id: string
  name: string
  status: string
  type: string
  value: number | null
}

interface PointsOverview {
  balance: number
  expiringSoon: number
  settings: { redeemPoints: number; redeemCents: number; maxDeductBps: number }
}

const kindLabels = { single: '单次套餐', multi: '多次卡', combo: '组合套餐' } as const
const yuan = (cents: number) => `¥${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`

/**
 * 官网团购商城：浏览套餐、加入购物车、生成报价并创建父交易。
 * 浏览器内支付属于部署依赖：未配置或缺少微信 openid 时明确提示在小程序完成支付。
 */
export function CommerceShop({ onRequireLogin, refreshToken = 0 }: { onRequireLogin: () => void; refreshToken?: number }) {
  const [products, setProducts] = useState<ProductSummary[]>([])
  const [detail, setDetail] = useState<ProductDetail | null>(null)
  const [skuId, setSkuId] = useState('')
  const [storeId, setStoreId] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [cartOpen, setCartOpen] = useState(false)
  const [cart, setCart] = useState<CartItem[]>([])
  const [quote, setQuote] = useState<Quote | null>(null)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  /** R09：官网结算与小程序同能力——可选券、可用积分抵扣。 */
  const [vouchers, setVouchers] = useState<CartVoucher[]>([])
  const [points, setPoints] = useState<PointsOverview | null>(null)
  const [couponId, setCouponId] = useState('')
  const [pointsToUse, setPointsToUse] = useState('')

  const authHeaders = () => {
    const session = readWebSession()
    return session ? { Authorization: `Bearer ${session.accessToken}` } : null
  }

  useEffect(() => {
    void requestApi<{ items: ProductSummary[] }>('/commerce/products')
      .then((data) => setProducts(data.items))
      .catch(() => setProducts([]))
  }, [refreshToken])

  const openDetail = async (id: string) => {
    try {
      const data = await requestApi<ProductDetail>(`/commerce/products/${id}`)
      setDetail(data)
      setSkuId(data.skus[0]?.id ?? '')
      setStoreId(data.storeScopes[0]?.storeId ?? '')
      setQuantity(1)
      setMessage('')
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '商品加载失败')
    }
  }

  /** 券与积分在两种入口（加购后弹窗、工具栏打开购物车）都要加载，否则结算少入口。 */
  const loadCartOptions = async (headers: { Authorization: string }) => {
    const [voucherList, pointsOverview] = await Promise.all([
      requestApi<CartVoucher[]>('/me/vouchers', { headers }).catch(() => [] as CartVoucher[]),
      requestApi<PointsOverview>('/me/points', { headers }).catch(() => null)
    ])
    setVouchers(voucherList.filter((item) => item.status === 'available' && item.type === 'cash'))
    setPoints(pointsOverview)
    setCouponId('')
    setPointsToUse('')
  }

  const addToCart = async () => {
    const headers = authHeaders()
    if (!headers) { onRequireLogin(); return }
    if (!detail || !skuId || !storeId) return
    setBusy(true)
    try {
      const data = await requestApi<{ items: CartItem[] }>('/me/cart/items', {
        method: 'PUT',
        headers,
        body: JSON.stringify({ skuId, quantity, salesStoreId: storeId })
      })
      setCart(data.items)
      setDetail(null)
      setCartOpen(true)
      setMessage('已加入购物车')
      await loadCartOptions(headers)
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '加入购物车失败')
    } finally {
      setBusy(false)
    }
  }

  const openCart = async () => {
    const headers = authHeaders()
    if (!headers) { onRequireLogin(); return }
    try {
      const data = await requestApi<{ items: CartItem[] }>('/me/cart', { headers })
      setCart(data.items)
      setQuote(null)
      setMessage('')
      setCartOpen(true)
      await loadCartOptions(headers)
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '购物车加载失败')
    }
  }

  const createQuote = async () => {
    const headers = authHeaders()
    if (!headers || !cart.length) return
    setBusy(true)
    setMessage('')
    try {
      const result = await requestApi<Quote & { invalidItems?: Array<{ reason: string }> }>('/checkout/quotes', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: cart.map((item) => ({ skuId: item.skuId, quantity: item.quantity, salesStoreId: item.salesStoreId })),
          couponIds: couponId ? [couponId] : [],
          pointsToUse: Number(pointsToUse) > 0 ? Math.floor(Number(pointsToUse)) : 0
        })
      })
      if (!result.quoteId) {
        setMessage(result.invalidItems?.[0]?.reason ?? '部分商品不可购买')
        return
      }
      setQuote(result)
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '结算失败')
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    const headers = authHeaders()
    if (!headers || !quote) return
    setBusy(true)
    try {
      const transaction = await requestApi<{ transactionNo: string }>('/shopping-transactions', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          quoteId: quote.quoteId,
          cartVersion: quote.cartVersion,
          idempotencyKey: `web-order-${quote.quoteId}-${Date.now()}`
        })
      })
      setQuote(null)
      setCart([])
      setCartOpen(false)
      setMessage(`订单 ${transaction.transactionNo} 已创建。请在微信小程序「我的订单」中完成支付（浏览器内支付属于上线依赖）。`)
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '下单失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="commerce-shop">
      <div className="commerce-shop__grid">
        {products.map((product) => (
          <article className="shop-card" key={product.id}>
            <button className="shop-card__cover" type="button" onClick={() => void openDetail(product.id)}>
              {product.coverUrl ? <img src={product.coverUrl} alt={product.name} loading="lazy" /> : <span>汀</span>}
            </button>
            <div className="shop-card__body">
              <small>{kindLabels[product.packageKind]} · 已售 {product.soldCount}</small>
              <h3>{product.name}</h3>
              <p>{product.subtitle || '到店体验，分次核销'}</p>
              <div><strong>{yuan(product.minPriceCents)} 起</strong><button type="button" onClick={() => void openDetail(product.id)}>查看详情</button></div>
            </div>
          </article>
        ))}
        {!products.length && <p className="commerce-shop__empty">套餐正在整理中，敬请期待。</p>}
      </div>
      <div className="commerce-shop__toolbar">
        <button type="button" className="button button--light" onClick={() => void openCart()}>购物车{cart.length ? `（${cart.length}）` : ''}</button>
      </div>
      {message && <p className="commerce-shop__message">{message}</p>}

      {detail && (
        <div className="shop-modal" role="dialog" aria-modal="true" onMouseDown={() => setDetail(null)}>
          <div className="shop-modal__card" onMouseDown={(event) => event.stopPropagation()}>
            <header><h3>{detail.name}</h3><button type="button" onClick={() => setDetail(null)} aria-label="关闭">×</button></header>
            <p>{detail.description || detail.subtitle}</p>
            <div className="shop-modal__group"><span>选择规格</span>
              {detail.skus.map((sku) => (
                <button
                  key={sku.id}
                  type="button"
                  className={skuId === sku.id ? 'is-active' : ''}
                  onClick={() => setSkuId(sku.id)}
                >{sku.name} · {yuan(sku.priceCents)}{sku.validDays ? ` · ${sku.validDays} 天` : ''}</button>
              ))}
            </div>
            <div className="shop-modal__group"><span>适用门店</span>
              {detail.storeScopes.map((store) => (
                <button
                  key={store.storeId}
                  type="button"
                  className={storeId === store.storeId ? 'is-active' : ''}
                  onClick={() => setStoreId(store.storeId)}
                >{store.name}</button>
              ))}
            </div>
            <div className="shop-modal__group"><span>数量</span>
              <div className="shop-modal__quantity">
                <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))}>－</button>
                <b>{quantity}</b>
                <button type="button" onClick={() => setQuantity((value) => Math.min(99, value + 1))}>＋</button>
              </div>
            </div>
            <footer>
              <strong>{yuan((detail.skus.find((sku) => sku.id === skuId)?.priceCents ?? 0) * quantity)}</strong>
              <button type="button" className="button button--dark" disabled={busy} onClick={() => void addToCart()}>加入购物车</button>
            </footer>
          </div>
        </div>
      )}

      {cartOpen && (
        <div className="shop-modal" role="dialog" aria-modal="true" onMouseDown={() => setCartOpen(false)}>
          <div className="shop-modal__card" onMouseDown={(event) => event.stopPropagation()}>
            <header><h3>购物车</h3><button type="button" onClick={() => setCartOpen(false)} aria-label="关闭">×</button></header>
            {cart.map((item) => (
              <div className="shop-cart__line" key={item.id}>
                <span>{item.productName} · {item.skuName}<small>{item.salesStoreName} × {item.quantity}{item.purchaseable ? '' : '（不可购买）'}</small></span>
                <strong>{yuan(item.priceCents * item.quantity)}</strong>
              </div>
            ))}
            {!cart.length && <p>购物车为空</p>}
            {!!cart.length && (
              <div className="shop-cart__options">
                <div className="shop-cart__option">
                  <span>优惠券</span>
                  <select value={couponId} onChange={(event) => { setCouponId(event.target.value); setQuote(null) }}>
                    <option value="">不使用优惠券</option>
                    {vouchers.map((voucher) => (
                      <option key={voucher.id} value={voucher.id}>
                        {voucher.name}{voucher.value !== null ? `（-${yuan(voucher.value)}）` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="shop-cart__option">
                  <span>积分抵扣{points ? `（可用 ${points.balance} 分）` : ''}</span>
                  <input
                    type="number"
                    min={0}
                    max={points?.balance ?? 0}
                    placeholder={points ? `最多 ${points.balance} 分` : '暂无积分'}
                    value={pointsToUse}
                    onChange={(event) => { setPointsToUse(event.target.value); setQuote(null) }}
                  />
                </div>
                {points && (
                  <small className="shop-cart__hint">
                    {points.settings.redeemPoints} 积分抵 {yuan(points.settings.redeemCents)}；
                    单笔最多抵扣 {(points.settings.maxDeductBps / 100).toFixed(0)}%，超出部分会自动收敛。
                  </small>
                )}
              </div>
            )}
            {quote && (
              <div className="shop-cart__quote">
                {quote.subOrders.map((sub) => (
                  <div key={sub.salesStoreId}><span>{sub.salesStoreName}</span><strong>{yuan(sub.cashCents)}</strong></div>
                ))}
                {quote.couponTotalCents > 0 && <div><span>优惠券抵扣</span><strong>-{yuan(quote.couponTotalCents)}</strong></div>}
                {quote.pointsValueCents > 0 && (
                  <div><span>积分抵扣（{quote.pointsUsed} 分）</span><strong>-{yuan(quote.pointsValueCents)}</strong></div>
                )}
                <div className="shop-cart__total"><span>现金应付</span><strong>{yuan(quote.cashTotalCents)}</strong></div>
              </div>
            )}
            <footer>
              {quote
                ? <button type="button" className="button button--dark" disabled={busy} onClick={() => void submit()}>{busy ? '处理中…' : '提交订单'}</button>
                : <button type="button" className="button button--dark" disabled={busy || !cart.length} onClick={() => void createQuote()}>{busy ? '计算中…' : '去结算'}</button>}
            </footer>
          </div>
        </div>
      )}
    </div>
  )
}
